import type { BulletChartProps } from '@usealloy/component-library';
import type { OutputValueRange, ValueRangeConfigOptions } from 'flow/store/engine/types';
import { HighRangeOption, LowRangeOption } from 'flow/service/actions';

export const createNewValueRangeConfig = (
  { id, max, min }: { id: string; max?: number; min?: number },
) => ({
  id,
  minValue: min ?? null,
  maxValue: max ?? null,
  label: '',
  color: '',
});

export const getUpdatedOutputValueRange = (
  {
    valueRangeConfigOptions: { highRange, lowRange }, outputValueRange,
  }: {
    valueRangeConfigOptions: ValueRangeConfigOptions; outputValueRange: OutputValueRange[];
  },
): OutputValueRange[] => {
  if (outputValueRange.length === 0) return [];

  if (outputValueRange.length === 1) {
    return [{
      ...outputValueRange[0],
      ...(lowRange === LowRangeOption.unbounded && { minValue: null }),
      ...(highRange === HighRangeOption.unbounded && { maxValue: null }),
    }];
  }

  return [
    {
      ...outputValueRange[0],
      ...(lowRange === LowRangeOption.unbounded && { minValue: null }),
    },
    ...outputValueRange.slice(1, -1),
    {
      ...outputValueRange[outputValueRange.length - 1],
      ...(highRange === HighRangeOption.unbounded && { maxValue: null }),
    },
  ];
};

export const getUpdatedAxis = ({
  index, outputValueRange, updatedPreviousValue, updatedValue,
}) => {
  const isLowerAxisBound = index === 0;
  if (isLowerAxisBound) {
    return outputValueRange.toSpliced(index, 1, updatedValue);
  }

  const isUpperAxisBound = index === outputValueRange.length;
  if (isUpperAxisBound) {
    return outputValueRange.toSpliced(index - 1, 1, updatedPreviousValue);
  }

  return outputValueRange.toSpliced(index - 1, 2, updatedPreviousValue, updatedValue);
};

export const getRangePadding = (outputValueRange: OutputValueRange[]) => {
  const lowerBound = outputValueRange[0].minValue
    ?? outputValueRange[0].maxValue;
  const upperBound = outputValueRange[outputValueRange.length - 1].maxValue
    ?? outputValueRange[outputValueRange.length - 1].minValue;

  if (lowerBound === upperBound) {
    return 1;
  }
  return Math.ceil((upperBound - lowerBound) / (outputValueRange.length - 1));
};

export const getFormattedRangeLabel = ({
  minValue,
  maxValue,
  isFirstUnbounded,
  isLastUnbounded,
}: {
  minValue: number | null;
  maxValue: number | null;
  isFirstUnbounded: boolean;
  isLastUnbounded: boolean;
}) => {
  const minLabel = isFirstUnbounded
    ? '-∞'
    : minValue ?? '-∞';
  const maxLabel = isLastUnbounded
    ? '∞'
    : maxValue ?? '∞';
  return { minLabel, maxLabel };
};

export const calculateColorRanges = ({
  outputValueRange,
  outputValueRangeOptions,
  includePendingRanges = false,
}: {
  outputValueRange: OutputValueRange[];
  outputValueRangeOptions: ValueRangeConfigOptions;
  includePendingRanges?: boolean;
}): {
  colorRanges: BulletChartProps['colorRanges'];
  animatedRangeIndices: number[];
  hasAtLeastOneColor: boolean;
} => {
  const rangesWithColors = outputValueRange.filter(
    ({ color }) => Boolean(color && color.trim()),
  );
  const hasColor = rangesWithColors.length > 0;

  // Calculate rangePadding from ranges that have both min and max values
  const rangesWithValues = outputValueRange.filter(
    ({ minValue, maxValue }) => minValue !== null && maxValue !== null,
  );
  let calculatedPadding: number;
  if (rangesWithValues.length === 0) {
    calculatedPadding = 1;
  } else if (rangesWithValues.length === 1) {
    calculatedPadding = rangesWithValues[0].maxValue - rangesWithValues[0].minValue;
  } else {
    calculatedPadding = getRangePadding(outputValueRange);
  }
  const rangePadding = Number.isFinite(calculatedPadding) && calculatedPadding > 0
    ? calculatedPadding
    : 1;

  const ranges: BulletChartProps['colorRanges'] = [];
  const pendingIndices = [];
  let currentEndPosition = null;

  outputValueRange.forEach(({
    color, maxValue, minValue, label,
  }, index) => {
    const rangeHasColor = Boolean(color && color.trim());
    const isFirstRow = index === 0;
    const isLastRow = index === outputValueRange.length - 1;
    const isFirstUnbounded = isFirstRow
      && outputValueRangeOptions?.lowRange === LowRangeOption.unbounded;
    const isLastUnbounded = isLastRow
      && outputValueRangeOptions?.highRange === HighRangeOption.unbounded;

    // A range is valid (will have a label) if it has a color and the required values
    // First row unbounded: needs (min OR max) and color
    // Last row unbounded: needs min and color
    // Last row bounded: needs min AND max and color
    // Middle rows: needs min and color (maxValue can be null if it was previously valid)
    let isValid = false;
    if (rangeHasColor) {
      if (isFirstUnbounded) {
        isValid = minValue !== null || maxValue !== null;
      } else if (isLastUnbounded) {
        isValid = minValue !== null;
      } else if (isLastRow) {
        isValid = minValue !== null && maxValue !== null;
      } else {
        isValid = minValue !== null;
      }
    }

    if (isValid) {
      // Range has a color - use its actual values
      let from = minValue ?? (
        maxValue !== null ? maxValue - rangePadding : currentEndPosition ?? 0
      );
      let to = maxValue ?? (
        minValue !== null ? minValue + rangePadding : from + rangePadding
      );

      // Ensure valid range
      if (!Number.isFinite(from) || !Number.isFinite(to) || from >= to) {
        from = minValue ?? maxValue ?? currentEndPosition ?? 0;
        to = maxValue ?? from + rangePadding;
      }
      if (from >= to) {
        to = from + rangePadding;
      }

      // Format label - valid ranges show labels
      const minLabel = isFirstUnbounded
        ? '-∞'
        : minValue ?? '-∞';

      let maxLabel = null;
      if (isLastUnbounded) {
        maxLabel = '∞';
      } else if (isLastRow) {
        maxLabel = maxValue !== null ? String(maxValue) : '∞';
      } else {
        maxLabel = maxValue !== null ? String(maxValue) : null;
      }

      const formattedLabel = `${label || ''} ${minLabel} - ${maxLabel ?? ''}`.trim() || '';

      ranges.push({
        color: `#${color}`,
        label: formattedLabel,
        from,
        to,
      });
      currentEndPosition = currentEndPosition ? Math.max(currentEndPosition, to) : to;
    } else if (includePendingRanges) {
      // Pending range (incomplete or no color) - position sequentially after valid ranges
      const previousMaxValue = index > 0
        ? (outputValueRange[index - 1].maxValue ?? outputValueRange[index - 1].minValue)
        : null;

      let from = minValue ?? currentEndPosition ?? 0;
      if (minValue !== null) {
        from = Math.max(from, currentEndPosition ?? 0);
      }

      let to = maxValue ?? (previousMaxValue ?? from) + rangePadding;
      if (from >= to) {
        to = from + rangePadding;
      }

      // Pending ranges (blinking) don't show labels
      // Use selected color if present, otherwise gray
      ranges.push({
        color: rangeHasColor ? `#${color}` : '#808080',
        from,
        to,
        label: '',
      });

      // Mark as pending (except first row)
      if (index > 0) {
        pendingIndices.push(ranges.length - 1);
      }

      currentEndPosition = to;
    }
  });

  return {
    colorRanges: ranges,
    animatedRangeIndices: pendingIndices,
    hasAtLeastOneColor: hasColor,
  };
};

export const insertOneValueRange = ({ insertDirection, row, outputValueRange }: {
  insertDirection: 'before' | 'after';
  row: number;
  outputValueRange: OutputValueRange[];
}) => {
  const newOutputValueRange = structuredClone(outputValueRange);
  const configOptions = {
    id: outputValueRange[row]?.id ?? `${Number(outputValueRange[row - 1].id) + 1}`,
    ...(insertDirection === 'before' && outputValueRange[row] && { max: outputValueRange[row].minValue }),
    ...(insertDirection === 'after' && { min: outputValueRange[row - 1].maxValue }),
  };
  const newConfig = createNewValueRangeConfig({
    ...configOptions,
  });
  newOutputValueRange.splice(row, 0, newConfig);
  newOutputValueRange.forEach((valueRange, index) => {
    if (insertDirection === 'before' && index === row - 1) {
      newOutputValueRange[index].maxValue = null;
    }
    if (insertDirection === 'after' && index === row + 1) {
      newOutputValueRange[index].minValue = null;
    }
    if (index > row) {
      newOutputValueRange[index].id = `${Number(newOutputValueRange[index].id) + 1}`;
    }
  });
  return newOutputValueRange;
};

export const deleteOneValueRange = ({ row, outputValueRange, outputValueRangeOptions }: {
  row: number;
  outputValueRange: OutputValueRange[];
  outputValueRangeOptions: ValueRangeConfigOptions;
}) => {
  const newOutputValueRange = JSON.parse(JSON.stringify(outputValueRange));
  newOutputValueRange.splice(row, 1);

  // make sure that unbounded ends are correct after the deletion
  if (outputValueRangeOptions.lowRange === LowRangeOption.unbounded) {
    newOutputValueRange[0].minValue = null;
  }
  if (outputValueRangeOptions.highRange === HighRangeOption.unbounded) {
    newOutputValueRange[newOutputValueRange.length - 1].maxValue = null;
  }

  // adjust the maxValue of the row above deletion
  if (newOutputValueRange[row - 1] && newOutputValueRange[row]) {
    newOutputValueRange[row - 1].maxValue = newOutputValueRange[row].minValue;
  }

  newOutputValueRange.forEach((valueRange, index) => {
    if (index >= row) {
      newOutputValueRange[index].id = `${Number(newOutputValueRange[index].id) - 1}`;
    }
  });
  return newOutputValueRange;
};

export const getIsDeleteDisabled = ({ outputValueRange, outputValueRangeOptions }: {
  outputValueRange: OutputValueRange[];
  outputValueRangeOptions: ValueRangeConfigOptions;
}) => {
  if (
    outputValueRange.length === 1
    || (
      outputValueRangeOptions.highRange === HighRangeOption.unbounded
      && outputValueRangeOptions.lowRange === LowRangeOption.unbounded
      && outputValueRange.length === 2
    )
  ) return true;

  return false;
};
